/**
 * Controls the update of the list of suppliers
 * when questions it depends on are answered
 * @module embed/supplier_list
 */

import {
  nameSelector,
  hasDualFuel,
  hasEconomy7,
  getPostcode,
  getPayMethod
} from './form';
import { delegate } from '@/lib/events';
import { busyWhile } from '@/lib/busy';
import { renderOptions } from '@/lib/forms';
import { hide, show } from '@/lib/show_hide';
import { renderIsFilled } from './is_filled';
import withErrorRender from '@/lib/withErrorRender';
import withEvents from '@/lib/withEvents';
import { ERROR_MESSAGE } from '@/../packs/embed';
import { isPostcodeValid } from './form_validation';
import isEqual from 'lodash/isEqual';

const SELECTOR = '.js-supplier-list';
const SELECTOR_MAIN_SUPPLIERS = SELECTOR + '__mainSuppliers';
const SELECTOR_INPUT = SELECTOR + '__input';
const SELECTOR_LABELS = SELECTOR_MAIN_SUPPLIERS + ' label';
const SELECTOR_SELECT = SELECTOR + '__select';
const SELECTOR_VALUE_HOLDER = SELECTOR + '__value';
const ATTR_FUEL_TYPE = 'data-fuel-type';
export const EVENT_SUPPLIER_LIST_UPDATE = 'supplier-list-update';

/**
 * Renders a list of `<option>` tags based on the suppliers list
 * returned by the server
 * @param {Array} suppliers
 */
function renderOptionEls(suppliers) {
  return suppliers
    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
    .map(({ id, name }) => `<option value="${id}">${name}</option>`);
}

/**
 * Updates which radio buttons for the main suppliers are available
 * @param {HTMLInputElement[]} inputs The list of input elements
 * @param {Array} suppliers The list of suppliers returned by the server
 */
function updateRadioAvailability(inputs, suppliers) {
  inputs
    // Handle Rails-injected empty value field
    .filter(input => input.value)
    .forEach(input => {
      if (suppliers.some(supplier => supplier.id == input.value)) {
        show(input.parentNode);
      } else {
        hide(input.parentNode);
        input.checked = false;
      }
    });
}

function hideDuplicateSSERadio(labels) {
  const SSERadios = labels.filter(label => label.textContent.match(/SSE/));
  if (SSERadios.length > 1) {
    hide(SSERadios.pop().parentNode);
  }
}

function setupSupplierList(scope, el, apiClient, validator) {
  const fuelType = el.getAttribute(ATTR_FUEL_TYPE);
  const inputs = Array.from(el.querySelectorAll(SELECTOR_INPUT));
  const select = el.querySelector(SELECTOR_SELECT);
  const valueHolder = el.querySelector(SELECTOR_VALUE_HOLDER);
  const prompt = '<option value="">Please select…</option>';

  // Extract list of providers with inputs
  const providersWithInputs = inputs.map(el => parseInt(el.value, 10));

  let lastOptions = {};
  function loadSuppliers(options) {
    if (!options.postcode) return;
    if (!isPostcodeValid(options.postcode, validator)) return Promise.resolve();
    if (isEqual(lastOptions, options)) return Promise.resolve();
    lastOptions = options;

    el.disabled = true;
    return busyWhile(
      el,
      withErrorRender(
        withEvents(
          apiClient
            .getSupplierList(options)
            .then(suppliers => {
              if (suppliers.length > 0) {
                // Update the radio buttons
                updateRadioAvailability(inputs, suppliers);
                // Updates the select control
                const selectSuppliers = suppliers.filter(function(supplier) {
                  return providersWithInputs.indexOf(supplier.id) == -1;
                });
                const optionsList = renderOptionEls(selectSuppliers);
                optionsList.unshift(prompt);
                renderOptions(select, optionsList.join(''));
                return renderIsFilled(select);
              } else {
                throw new Error('No suppliers listed');
              }
            })
            .then(r => {
              el.disabled = false;
              return r;
            }),
          {
            scope
          }
        ),
        {
          scope: el,
          message: ERROR_MESSAGE,
          beforeSelector: SELECTOR_MAIN_SUPPLIERS
        }
      )
    );
  }

  hideDuplicateSSERadio(Array.from(el.querySelectorAll(SELECTOR_LABELS)));

  // Synchronises the list and the checkboxes
  el.addEventListener(
    'change',
    delegate(`${SELECTOR_INPUT},${SELECTOR_SELECT}`, ({ target }) => {
      if (target.tagName === 'INPUT') {
        select.value = '';
      } else {
        const checkedRadio = el.querySelector(`${SELECTOR_INPUT}:checked`);
        if (checkedRadio) {
          checkedRadio.checked = false;
        }
      }
      valueHolder.value = target.value;
    })
  );

  function updateAvailableSuppliers() {
    loadSuppliers({
      postcode: getPostcode(scope),
      economy7: fuelType == 'gas' ? false : hasEconomy7(scope),
      fuel_type: fuelType,
      dual_fuel: hasDualFuel(scope),
      current_pay_method: getPayMethod(scope, fuelType)
    });
  }

  let timeout;
  scope.addEventListener(
    'change',
    delegate(
      `${nameSelector('postcode')},
       ${nameSelector('economy7')},
       ${nameSelector('current_pay_method', fuelType)},
       ${nameSelector('dual_fuel')},
       ${nameSelector('fuel_type')}`,
      () => {
        clearTimeout(timeout);
        timeout = setTimeout(updateAvailableSuppliers, 0);
      }
    )
  );

  scope.addEventListener(
    'keydown',
    delegate(
      `${nameSelector('postcode')}`,
      event => {
        if (event.keyCode == 13) {
          clearTimeout(timeout);
          // Instantly update tariffs
          updateAvailableSuppliers();
        }
      },
      // Make sure this happens before submission even starts
      true
    )
  );

  scope.addEventListener(
    'input',
    delegate(`${nameSelector('postcode')}`, () => {
      clearTimeout(timeout);
      timeout = setTimeout(updateAvailableSuppliers, 1000);
    })
  );
  // TODO: Expect this to have been already taken care off by the backend
  updateAvailableSuppliers();
}

/**
 * General setup
 * @param {HTMLElement} el The wrapper into which to look for the suppliers list field
 * @param {*} apiClient The API client
 * @param {*} validator The form validator
 */
export default function setupSupplierLists(el, apiClient, validator) {
  Array.from(el.querySelectorAll(SELECTOR)).forEach(widget =>
    setupSupplierList(el, widget, apiClient, validator)
  );
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/supplier_list.js